import { RefObject, useEffect } from "react";

import { FASTDRAW_URL } from "../redux/constants";
import { useAppDispatch, useAppSelector } from "../redux/hooks";
import { userLoggedInSynergy } from "../redux/slices";
import CacheUtils from "../utils/cache-utils";

export interface useSynergyAuthProps {
  synergyForm: RefObject<HTMLFormElement>;
  setShowSynergyModal: (show: boolean) => void;
}

export const useSynergyAuth = ({ synergyForm, setShowSynergyModal }: useSynergyAuthProps) => {
  const auth = useAppSelector((state) => state.auth);
  const dispatch = useAppDispatch();

  const checkToken = () => {
    const synergyTokenExpiresAt = parseInt(CacheUtils.getSynergyTokenExpiration() || "0") || 0;
    const currentSeconds = Math.floor(Date.now() / 1000);

    return synergyTokenExpiresAt - currentSeconds > 1800;
  };

  const refreshSynergyToken = async () => {
    const refreshParams = new URLSearchParams("");
    refreshParams.append("bearer_token", auth.token || "");
    refreshParams.append("synergy_token", auth.synergyToken || "");
    refreshParams.append("min_ttl", "1800");

    const headers = new Headers();
    headers.set("Content-Type", "application/x-www-form-urlencoded");
    headers.set("Authorization", `Bearer ${auth.token}`);

    try {
      const response = await fetch(`${FASTDRAW_URL}/auth/synergy/refresh`, {
        method: "POST",
        body: refreshParams,
        headers,
      });

      const data = await response.json();

      if (data.error || data.expires_in < 1800) {
        // Refresh failed. Clear out token saved information.
        CacheUtils.clearSynergyCache();
        throw new Error("Refresh failed");
      } else {
        CacheUtils.clearSynergyCache();
        CacheUtils.setSynergyToken(data.synergy_token);
        CacheUtils.setSynergyTokenExpiration(Math.floor(Date.now() / 1000) + parseInt(data.expires_in));
        dispatch(userLoggedInSynergy());
      }

      return data;
    } catch (error) {
      console.error(error);
      return error;
    }
  };

  useEffect(() => {
    // Add event listener to listen for messages from the child window
    window.addEventListener("message", receiveMessage);

    return () => {
      // Remove the event listener when the component unmounts
      window.removeEventListener("message", receiveMessage);
    };
  }, []);

  // Function to handle messages received from the child window
  const receiveMessage = (event: any) => {
    const messageData = event.data;
    if (messageData.type === "synergy-auth-trigger") {
      // user has logged in to Synergy already, submit form to complete the flow on FD side
      if (synergyForm.current) {
        synergyForm.current.submit();
      }
    }
  };

  const handleSynergyClick = async () => {
    const isAccessedViaIframe = window.self !== window.top;

    if (auth.synergyToken) {
      if (checkToken()) {
        setShowSynergyModal(true);
      } else {
        if (isAccessedViaIframe) {
          // send message to FastScout to trigger synergy auth flow
          window.parent.postMessage({ type: "synergy-auth" }, "*");
        } else {
          // refresh the synergy token
          const response = await refreshSynergyToken();
          // if response is 400 we need to redo the synergy auth flow
          if (response.name === "Error") {
            synergyForm.current?.submit();
          } else {
            setShowSynergyModal(true);
          }
        }
      }
    } else {
      if (isAccessedViaIframe) {
        // send message to FastScout to trigger synergy auth flow
        window.parent.postMessage({ type: "synergy-auth-trigger" }, "*");
      } else {
        synergyForm.current?.submit();
      }
    }
  };

  return { handleSynergyClick };
};
